import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import PublicIcon from '@mui/icons-material/Public';
import {Divider,} from '@mui/material';
import React from 'react';
import {EmptyPageContent} from '../../../components/EmptyPageContent';
import {Pagination} from '../../../components/Pagination';
import {SupportSlotAssignmentTypeEnum} from '../../../model/support/slot/support-slot-assignment-type.enum';
import {SupportSlotWeekDayComponent} from '../shared/SupportSlotWeekDayComponent';
import {SupportPlanDetailsHeader} from './components/SupportPlanDetailsHeader';
import {SupportPlanSlotPreviewDrawer} from './components/SupportPlanSlotPreviewDrawer';
import {SupportPlanCalendar} from './components/SupportPlanTaskCalendar';
import {SupportPlanUpdateDrawer} from './components/SupportPlanUpdateDrawer';
import {SupportTaskCreateDrawer} from './components/SupportTaskCreateDrawer';
import {SupportTaskHeader} from './components/SupportTaskHeader';
import {SupportTaskTable} from './components/SupportTaskTable';
import {SupportTaskUpdateDrawer} from './components/SupportTaskUpdateDrawer';
import {useSupportPlanPageState} from './state/support-plan-page-state';
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

type Props = {
  organizationId: string;
};

export const SupportPlanPage = ({organizationId}: Props) => {
  const state = useSupportPlanPageState(organizationId)

  if (!state.supportPlan) {
    return <EmptyPageContent description={'Brak skonfigurowanego wsparcia technicznego'}/>
  }

  const buildSlotIcon = (slotDefinitionId: string) => {
    const assignmentType = state.extractSlotAssignmentType(slotDefinitionId)

    switch (assignmentType) {
      case SupportSlotAssignmentTypeEnum.GENERAL:
        return <AccessTimeIcon fontSize="small" />;
      case SupportSlotAssignmentTypeEnum.ANALYSIS:
        return <QueryStatsIcon fontSize="small" />;
      case SupportSlotAssignmentTypeEnum.CONSULTATION:
        return <HeadsetMicOutlinedIcon fontSize="small" />;
      default:
        return <PublicIcon fontSize="small" />;
    }
  }

  return <>
    <SupportPlanDetailsHeader
      supportPlan={state.supportPlan}
      translate={state.translate}
      translateLocal={state.translateLocal}
      onDrawerOpened={() => state.setDrawerOpen(true)}
    />
    <Divider sx={{ my: 2 }} />
    <SupportSlotWeekDayComponent
      height={200}
      slots={state.slotDefinitions}
      onSlotPressed={(slot) => state.setSlotPreviewDrawerOpen(true, slot.id)}
      buildSlotIcon={buildSlotIcon}
    />
    <Divider sx={{ my: 2 }} />
    <SupportTaskHeader
      translate={state.translate}
      searchableState={state.searchableState}
      searchText={state.searchableState.fullTextSearch}
      onSearchText={state.searchableState.onFullTextSearch}
      extractAvailableValues={state.extractAvailableValues}
      viewMode={state.viewMode}
      onViewModeChanged={state.onViewModeChanged}
      onAddPressed={() => state.setCreateTaskDrawerOpen(true, null)}
    />
    {
      state.viewMode === 'CALENDAR'
        ? <SupportPlanCalendar
          tasks={state.tasks}
          slots={state.slotDefinitionAssignments}
          onEventClick={(task) => state.setTaskPreviewDrawerOpen(true, task.id)}
          onSlotClick={(slot, startDate, endDate) => {
            state.setCreateTaskDrawerOpen(true, {slot, startDate, endDate})
          }}
          onCalendarDateRangeChanged={state.onCalendarDateRangeChanged}
        /> : <></>
    }
    {
      state.viewMode === 'LIST'
        ? <>
          <SupportTaskTable
            tasks={state.tasks}
            translate={state.translate}
            translateLocal={(key) => state.translate(`support.plan.task.${key}`)}
            onTaskPressed={(task) => state.setTaskPreviewDrawerOpen(true, task.id)}
            ordering={state.searchableState.searchable.ordering}
            onOrdering={state.searchableState.onOrdering}
          />
          <Pagination
            count={state.total}
            page={state.searchableState.searchable.page - 1}
            rowsPerPage={state.searchableState.searchable.pageSize}
            onPageChange={(page) => state.searchableState.onPage(page + 1)}
            onRowsPerPageChange={state.searchableState.onPageSize}
          />
        </>: <></>
    }
    {
      state.selectedSlotDefinitionAssignment && <SupportPlanSlotPreviewDrawer
        translate={state.translate}
        slotDefinitionAssignment={state.selectedSlotDefinitionAssignment}
        isDrawerOpen={state.slotPreviewDrawerOpen}
        onDrawerClosed={() => state.setSlotPreviewDrawerOpen(false, null)}
        onSlotAssignmentDeleted={state.onSlotAssignmentDeleted}
      />
    }
    {
      state.supportPlan && state.createTaskDrawerOpen && <SupportTaskCreateDrawer
        organizationId={organizationId}
        supportPlanId={state.supportPlan.id}
        initialContext={state.createTaskContext}
        isDrawerOpen={state.createTaskDrawerOpen}
        onDrawerClosed={() => state.setCreateTaskDrawerOpen(false, null)}
        refreshTasks={state.refreshTasks}
      />
    }
    {
      state.selectedTask && <SupportTaskUpdateDrawer
        organizationId={organizationId}
        supportPlanId={state.supportPlan.id}
        isDrawerOpen={state.taskPreviewDrawerOpen}
        task={state.selectedTask}
        onDrawerClosed={() => state.setTaskPreviewDrawerOpen(false, null)}
        refreshTasks={state.refreshTasks}
      />
    }
    <SupportPlanUpdateDrawer
      supportPlan={state.supportPlan}
      refreshSupportPlan={state.refreshSupportPlan}
      isDrawerOpen={state.drawerOpen}
      onDrawerClosed={() => state.setDrawerOpen(false)}
    />
  </>
}